import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { 
  MapPin, 
  Navigation, 
  Building2, 
  Award,
  Eye,
  Users,
  RefreshCw,
  ChevronRight,
  Handshake
} from 'lucide-react';
import { toast } from 'sonner';
import CollaborationRequestDialog from './CollaborationRequestDialog';

const API = BACKEND_URL;

const NearbyOrganizations = ({ actorId, actorName, onViewDetails }) => {
  const { t } = useTranslation();
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(50);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [hasCoordinates, setHasCoordinates] = useState(true);
  const [showCollabDialog, setShowCollabDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const fetchNearby = async () => {
    if (!actorId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/social-actors/${actorId}/nearby?radius_km=${radius}&limit=10`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setSourceInfo(response.data?.source || null);
      setNearby(Array.isArray(response.data?.nearby_organizations) ? response.data.nearby_organizations : []);
      setHasCoordinates(true);
    } catch (error) {
      console.error('Failed to fetch nearby organizations:', error);
      if (error.response?.status === 400) {
        setHasCoordinates(false);
        toast.error(t('nearby_orgs.error_no_geo'));
      } else {
        toast.error(t('nearby_orgs.error_load'));
      }
      setNearby([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNearby();
  }, [actorId]);

  const getCertificationBadge = (level) => {
    const badges = {
      'level_3_excellence': { label: t('nearby_orgs.cert_level_a'), color: 'bg-amber-100 text-amber-800', icon: '🏆' },
      'level_2_commitment': { label: t('nearby_orgs.cert_level_b'), color: 'bg-blue-100 text-blue-800', icon: '⭐' },
      'level_1_basic': { label: t('nearby_orgs.cert_level_c'), color: 'bg-green-100 text-green-800', icon: '✓' },
      'none': { label: t('nearby_orgs.cert_not_certified'), color: 'bg-gray-100 text-gray-600', icon: '' }
    };
    return badges[level] || badges['none'];
  };

  const formatDistance = (km) => {
    if (km < 1) {
      return t('nearby_orgs.distance_meters', { m: Math.round(km * 1000) });
    }
    return t('nearby_orgs.distance_km', { km: km.toFixed(1) });
  };

  if (!hasCoordinates) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-6">
          <div className="flex items-center gap-3 text-amber-700">
            <MapPin className="h-5 w-5" />
            <div>
              <p className="font-medium">{t('nearby_orgs.location_unavailable_title')}</p>
              <p className="text-sm text-amber-600">
                {t('nearby_orgs.location_unavailable_desc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-blue-600" />
            {t('nearby_orgs.title')}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchNearby}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('nearby_orgs.refresh')}
          </Button>
        </div>
        
        {/* Radius Slider */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-600">{t('nearby_orgs.search_radius')}</Label>
            <span className="text-sm font-medium text-blue-600">{radius} km</span>
          </div>
          <div className="flex items-center gap-4">
            <Slider
              value={[radius]}
              onValueChange={(value) => setRadius(value[0])}
              min={5}
              max={200}
              step={5}
              className="flex-1"
            />
            <Button 
              size="sm" 
              variant="secondary"
              onClick={fetchNearby}
              disabled={loading}
            >
              {t('nearby_orgs.search_btn')}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            {t('nearby_orgs.searching')}
          </div>
        ) : nearby.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>{t('nearby_orgs.no_results_title', { radius })}</p>
            <p className="text-sm mt-1">{t('nearby_orgs.no_results_hint')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-3">
              {t('nearby_orgs.found_count', { count: nearby.length, radius })}
            </p>
            
            {nearby.map((org) => {
              const cert = getCertificationBadge(org.certification_level);
              return (
                <div
                  key={org.id}
                  className="border rounded-lg p-3 hover:shadow-md transition-shadow bg-white"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <h4 className="font-medium text-sm truncate">
                          {org.organization_name}
                        </h4>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <MapPin className="h-3 w-3" />
                        <span>{org.city}, {org.country}</span>
                        <span className="text-gray-300">|</span>
                        <Navigation className="h-3 w-3 text-blue-500" />
                        <span className="font-medium text-blue-600">
                          {t('nearby_orgs.distance_away', { distance: formatDistance(org.distance_km) })}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${cert.color} text-xs px-2 py-0`}>
                          {cert.icon} {cert.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {org.organization_type?.replace('_', ' ')}
                        </Badge>
                        {org.sdg_alignment && org.sdg_alignment.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {t('nearby_orgs.sdgs_count', { count: org.sdg_alignment.length })}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {onViewDetails && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewDetails(org)}
                        className="flex-shrink-0"
                        title={t('nearby_orgs.view_details_tooltip')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedOrg(org);
                        setShowCollabDialog(true);
                      }}
                      className="flex-shrink-0 text-blue-700 hover:text-blue-700 hover:bg-blue-50"
                      title={t('nearby_orgs.request_collab_tooltip')}
                    >
                      <Handshake className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {org.social_mission && (
                    <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                      {org.social_mission}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Collaboration Request Dialog */}
      <CollaborationRequestDialog
        open={showCollabDialog}
        onOpenChange={setShowCollabDialog}
        targetOrganization={selectedOrg}
        onSuccess={() => {
          setSelectedOrg(null);
        }}
      />
    </Card>
  );
};

export default NearbyOrganizations;
